import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useMenuItemPopularity } from '@/hooks/useChartAnalytics'
import { useTranslation } from 'react-i18next'
import { ChefHat } from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'

interface MenuPopularityChartProps {
  restaurantId: string | null
  startDate: Date
  endDate: Date
}

// Colors for the pie chart
const COLORS = [
  '#10b981', // green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
  '#84cc16', // lime
  '#14b8a6', // teal
]

interface ChartDataItem {
  name: string
  value: number
  percentage: string
  fill: string
}

export function MenuPopularityChart({ restaurantId, startDate, endDate }: MenuPopularityChartProps) {
  const { t, i18n } = useTranslation()
  const { data, isLoading } = useMenuItemPopularity(restaurantId, startDate, endDate)

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <ChefHat className="h-5 w-5" />
            {t('analytics.menuPopularity')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[250px] flex items-center justify-center">
            <div className="w-32 h-32 rounded-full bg-muted animate-pulse" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <ChefHat className="h-5 w-5" />
            {t('analytics.menuPopularity')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[250px] flex items-center justify-center">
            <p className="text-muted-foreground">{t('analytics.noMenuData')}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Calculate total quantity for percentage
  const totalQuantity = data.reduce((sum, item) => sum + item.quantitySold, 0)

  // Transform data for recharts
  const chartData: ChartDataItem[] = data.map((item, index) => {
    const name = i18n.language === 'ar' ? item.menuItemNameAr : item.menuItemNameFr
    return {
      name: name || item.menuItemNameFr || item.menuItemNameAr,
      value: item.quantitySold,
      percentage: totalQuantity > 0 ? ((item.quantitySold / totalQuantity) * 100).toFixed(1) : '0',
      fill: COLORS[index % COLORS.length]!,
    }
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomLabel = (props: any) => {
    return `${props.percentage}%`
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <ChefHat className="h-5 w-5" />
          {t('analytics.menuPopularity')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                label={renderCustomLabel}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => [
                  `${value} ${t('analytics.quantitySold')}`,
                ]}
              />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
